import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import { database } from '../../firebaseConfig';
import { ref, onValue, push, remove, update, get, set } from 'firebase/database';
import './BusinessAnalysisDashboard.css';

function BusinessAnalysisDashboard() {
    const navigate = useNavigate();
    const [analyses, setAnalyses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showJsonModal, setShowJsonModal] = useState(false);
    const [selectedAnalysisJson, setSelectedAnalysisJson] = useState(null);

    useEffect(() => {
        if (auth.currentUser) {
            const userId = auth.currentUser.uid;
            const analysesRef = ref(database, `businessAnalyses/${userId}`);
            
            console.log('🔍 BusinessAnalysisDashboard: Loading analyses for user:', userId);
            console.log('🔍 Firebase path:', `businessAnalyses/${userId}`);
            
            const unsubscribe = onValue(analysesRef, (snapshot) => {
                try {
                    console.log('🔍 Snapshot exists:', snapshot.exists());
                    if (snapshot.exists()) {
                        const data = snapshot.val();
                        console.log('✅ Raw analyses data:', data);
                        console.log('✅ Number of analyses:', Object.keys(data).length);
                        
                        const analysesArray = Object.keys(data).map(key => {
                            const analysis = data[key];
                            return {
                                id: key,
                                ...analysis, // Alle Analyse-Daten einschließen
                                name: analysis.name || 'Unbenannte Analyse',
                                brandType: analysis.brandType || 'personal',
                                niche: analysis.niche || '',
                                targetAudience: analysis.targetAudience || '',
                                marketSaturation: analysis.marketSaturation || 'under100',
                                status: analysis.status || 'draft',
                                createdAt: analysis.createdAt || new Date().toISOString(),
                                updatedAt: analysis.updatedAt || analysis.createdAt || new Date().toISOString(),
                                // Include summary data for display
                                leadSourcesCount: analysis.leadSources?.length || 0,
                                productsCount: analysis.products?.length || 0,
                                completionScore: calculateCompletionScore(analysis)
                            };
                        });
                        
                        // Sort by most recently updated
                        const sortedAnalyses = analysesArray.sort((a, b) => 
                            new Date(b.updatedAt) - new Date(a.updatedAt)
                        );
                        
                        console.log(`✅ Loaded ${sortedAnalyses.length} analyses:`, sortedAnalyses);
                        setAnalyses(sortedAnalyses);
                    } else {
                        console.log('⚠️ No analyses found for user');
                        console.log('⚠️ Snapshot value:', snapshot.val());
                        setAnalyses([]);
                    }
                } catch (error) {
                    console.error('❌ Error processing analyses data:', error);
                    setAnalyses([]);
                }
                setIsLoading(false);
            }, (error) => {
                console.error('❌ Firebase listener error:', error);
                console.error('❌ Error details:', {
                    code: error.code,
                    message: error.message
                });
                setAnalyses([]);
                setIsLoading(false);
            });

            return () => unsubscribe();
        } else {
            console.log('⚠️ No authenticated user found');
            console.log('⚠️ auth.currentUser:', auth.currentUser);
            setIsLoading(false);
        }
    }, []);

    // Calculate completion score based on filled fields
    const calculateCompletionScore = (analysis) => {
        if (!analysis) return 0;
        
        let totalFields = 0;
        let filledFields = 0;
        
        // Basic fields (8 fields)
        const basicFields = ['name', 'brandType', 'niche', 'targetAudience', 'marketSaturation', 'geoFocus', 'language', 'foundingDate'];
        basicFields.forEach(field => {
            totalFields++;
            if (analysis[field] && analysis[field].toString().trim()) {
                filledFields++;
            }
        });
        
        // Branding fields (7 fields)
        if (analysis.branding) {
            const brandingFields = ['embodiment', 'emotionalTriggers', 'brandingQuality', 'targetGender', 'messageDesignAlignment', 'photoQuality', 'videoMaterial'];
            brandingFields.forEach(field => {
                totalFields++;
                if (analysis.branding[field] !== undefined && analysis.branding[field] !== '') {
                    filledFields++;
                }
            });
        } else {
            totalFields += 7; // Add branding fields to total even if section doesn't exist
        }
        
        // Lead sources (at least one with source and strength)
        totalFields++;
        if (analysis.leadSources && analysis.leadSources.length > 0 && analysis.leadSources[0].source) {
            filledFields++;
        }
        
        // Products (at least one with name)
        totalFields++;
        if (analysis.products && analysis.products.length > 0 && analysis.products[0].name?.trim()) {
            filledFields++;
        }
        
        return Math.round((filledFields / totalFields) * 100);
    };


    const handleDeleteAnalysis = async (analysisId) => {
        if (window.confirm('Are you sure you want to delete this analysis?')) {
            try {
                const analysisRef = ref(database, `businessAnalyses/${auth.currentUser.uid}/${analysisId}`);
                await remove(analysisRef);
            } catch (error) {
                console.error('Error deleting analysis:', error);
            }
        }
    };

    const cleanJsonData = (data) => {
        // Erstelle eine tiefe Kopie der Daten
        const cleanedData = JSON.parse(JSON.stringify(data));
        
        // Funktion zum Entfernen von false Features
        const removeFalseFeatures = (obj) => {
            if (obj && typeof obj === 'object') {
                // Wenn es ein features-Objekt ist, filtere false Werte raus
                if ('features' in obj && typeof obj.features === 'object') {
                    const filteredFeatures = {};
                    Object.keys(obj.features).forEach(key => {
                        if (obj.features[key] === true) {
                            filteredFeatures[key] = true;
                        }
                    });
                    // Nur features Objekt behalten wenn es nicht leer ist
                    if (Object.keys(filteredFeatures).length > 0) {
                        obj.features = filteredFeatures;
                    } else {
                        delete obj.features;
                    }
                }
                
                // Rekursiv durch alle verschachtelten Objekte gehen
                Object.keys(obj).forEach(key => {
                    if (typeof obj[key] === 'object' && obj[key] !== null) {
                        if (Array.isArray(obj[key])) {
                            // Wenn es ein Array ist, jeden Eintrag durchgehen
                            obj[key] = obj[key].map(item => {
                                if (typeof item === 'object' && item !== null) {
                                    removeFalseFeatures(item);
                                }
                                return item;
                            });
                        } else {
                            removeFalseFeatures(obj[key]);
                        }
                    }
                });
            }
            return obj;
        };
        
        // Sortiere die Keys in einer logischen Reihenfolge
        const sortKeys = (obj) => {
            // Definiere die gewünschte Reihenfolge der Hauptfelder
            const keyOrder = [
                'name',
                'niche',
                'brandType',
                'geoFocus',
                'language',
                'marketSaturation',
                'foundingDate',
                'targetAudience',
                'branding',
                'leadSources',
                'products',
                'socialProof',
                'socialMedia',
                'status',
                'createdAt',
                'updatedAt',
                'shareToken',
                'publishedAt'
            ];
            
            const sorted = {};
            
            // Zuerst die Keys in der definierten Reihenfolge hinzufügen
            keyOrder.forEach(key => {
                if (key in obj) {
                    sorted[key] = obj[key];
                }
            });
            
            // Dann alle anderen Keys hinzufügen, die nicht in der Liste sind
            Object.keys(obj).forEach(key => {
                if (!(key in sorted)) {
                    sorted[key] = obj[key];
                }
            });
            
            return sorted;
        };
        
        const cleaned = removeFalseFeatures(cleanedData);
        return sortKeys(cleaned);
    };

    const handleShowJson = async (analysisId) => {
        try {
            const analysisRef = ref(database, `businessAnalyses/${auth.currentUser.uid}/${analysisId}`);
            const snapshot = await get(analysisRef);
            
            if (snapshot.exists()) {
                const analysisData = snapshot.val();
                const cleanedData = cleanJsonData(analysisData);
                setSelectedAnalysisJson(cleanedData);
                setShowJsonModal(true);
            }
        } catch (error) {
            console.error('Error loading analysis JSON:', error);
            alert('Fehler beim Laden der Analyse-Daten');
        }
    };

    const handleCopyJson = () => {
        const jsonString = JSON.stringify(selectedAnalysisJson, null, 2);
        navigator.clipboard.writeText(jsonString).then(() => {
            alert('JSON in Zwischenablage kopiert! ✅');
        }).catch(err => {
            console.error('Fehler beim Kopieren:', err);
            alert('Fehler beim Kopieren in die Zwischenablage');
        });
    };

    const handleCloseJsonModal = () => {
        setShowJsonModal(false);
        setSelectedAnalysisJson(null);
    };

    const handleViewAnalysis = async (analysisId) => {
        try {
            console.log('🔍 Starting to publish analysis:', analysisId);
            
            if (!auth.currentUser) {
                console.error('❌ No authenticated user');
                alert('Bitte melde dich an, um die Vorschau anzuzeigen');
                return;
            }
            
            const userId = auth.currentUser.uid;
            console.log('✅ User ID:', userId);
            
            // Load the full analysis data
            const analysisRef = ref(database, `businessAnalyses/${userId}/${analysisId}`);
            console.log('📥 Loading analysis from:', `businessAnalyses/${userId}/${analysisId}`);
            
            const snapshot = await get(analysisRef);
            
            if (!snapshot.exists()) {
                console.error('❌ Analysis not found');
                alert('Analyse nicht gefunden');
                return;
            }
            
            const analysisData = snapshot.val();
            console.log('✅ Analysis data loaded:', analysisData.name);
            
            // Check if analysis already has a shareToken
            let shareToken = analysisData.shareToken;
            
            if (!shareToken) {
                // Generate a new shareToken
                shareToken = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                console.log('🔑 Generated new shareToken:', shareToken);
                
                // Update the analysis with the shareToken
                await update(analysisRef, { shareToken });
                console.log('✅ ShareToken saved to analysis');
            } else {
                console.log('🔑 Using existing shareToken:', shareToken);
            }
            
            // Publish/update the analysis to public path
            const publicRef = ref(database, `publicBusinessAnalyses/${shareToken}`);
            console.log('📤 Publishing to:', `publicBusinessAnalyses/${shareToken}`);
            
            await set(publicRef, {
                ...analysisData,
                shareToken,
                publishedAt: new Date().toISOString()
            });
            
            console.log('✅ Analysis published successfully');
            
            // Navigate to the public URL
            const publicUrl = `/business-analysis/public/${shareToken}`;
            console.log('🔗 Navigating to:', publicUrl);
            navigate(publicUrl);
            
        } catch (error) {
            console.error('❌ Error publishing analysis:', error);
            console.error('❌ Error details:', error.message);
            alert(`Fehler beim Öffnen der Vorschau: ${error.message}`);
        }
    };

    if (isLoading) {
        return (
            <div className="business-analyse-loading">
                <div className="business-analyse-spinner"></div>
                <p>Loading your business analyses...</p>
            </div>
        );
    }

    return (
        <div className="business-analyse-dashboard">
            {/* Header Section */}
            <div className="business-analyse-header">
                <div className="business-analyse-header-content">
                    <div className="business-analyse-title-section">
                        <h1 className="business-analyse-main-title">
                            <img src="/img/xraay.svg" alt="XRAAY" className="business-analyse-logo" />
                            <span className="business-analyse-ai-badge">AI</span>
                        </h1>
                        <p className="business-analyse-subtitle">
                            Create and manage comprehensive business analyses to drive strategic decisions
                        </p>
                    </div>
                    <div className="business-analyse-header-actions">
                        <button 
                            className="business-analyse-compare-btn"
                            onClick={() => navigate('/business-analysis/compare')}
                        >
                            <i className="fas fa-balance-scale"></i>
                            Vergleichen
                        </button>
                        <button 
                            className="business-analyse-add-btn"
                            onClick={() => navigate('/business-analysis/add')}
                        >
                            <i className="fas fa-plus"></i>
                            Neue Analyse
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="business-analyse-content">
                {analyses.length === 0 ? (
                    <div className="business-analyse-empty-state">
                        <div className="business-analyse-empty-icon">
                            <i className="fas fa-chart-line"></i>
                        </div>
                        <h3>No Business Analyses Yet</h3>
                        <p>Start by creating your first business analysis to organize your strategic insights and planning.</p>
                        <button 
                            className="business-analyse-empty-cta"
                            onClick={() => navigate('/business-analysis/add')}
                        >
                            <i className="fas fa-plus"></i>
                            Create Your First Analysis
                        </button>
                    </div>
                ) : (
                    <div className="business-analyse-grid">
                        {analyses.map((analysis) => {
                            // Calculate business score for this analysis
                            const branding = analysis.branding || {};
                            const brandingScore = ((branding.brandingQuality || 5) + 
                                                  (branding.messageDesignAlignment || 5) + 
                                                  (branding.photoQuality || 5) + 
                                                  (branding.videoMaterial || 5)) / 4 * 10;
                            
                            const leadCount = analysis.leadSources?.length || 0;
                            const leadAvg = leadCount > 0 
                                ? analysis.leadSources.reduce((sum, s) => sum + (s.strength || 5), 0) / leadCount 
                                : 5;
                            const leadScore = Math.min((leadCount * 15) + (leadAvg * 5), 100);
                            
                            const productCount = analysis.products?.filter(p => p.name?.trim()).length || 0;
                            const hasHighTicket = analysis.products?.some(p => ['500-1000', '1000-5000', '5000-10000', '10000-20000', '20000-50000', 'over50000'].includes(p.cost)) || false;
                            const hasUltraHighTicket = analysis.products?.some(p => ['10000-20000', '20000-50000', 'over50000'].includes(p.cost)) || false;
                            const hasRecurring = analysis.products?.some(p => p.paymentType === 'monthly') || false;
                            const hasRevolutionary = analysis.products?.some(p => p.exclusivity === 'revolutionary') || false;
                            const productScore = Math.min((productCount * 15) + (hasHighTicket ? 25 : 0) + (hasUltraHighTicket ? 15 : 0) + (hasRecurring ? 30 : 0) + (hasRevolutionary ? 15 : 0), 100);
                            
                            const proofCount = analysis.socialProof?.length || 0;
                            const proofAvg = proofCount > 0 
                                ? analysis.socialProof.reduce((sum, p) => sum + (p.strength || 5), 0) / proofCount 
                                : 5;
                            const proofScore = Math.min((proofCount * 12) + (proofAvg * 6), 100);
                            
                            const overallScore = Math.round(
                                (brandingScore * 0.30) +
                                (leadScore * 0.30) +
                                (productScore * 0.20) +
                                (proofScore * 0.20)
                            );
                            
                            const scoreColor = overallScore >= 80 ? '#00ff88' : 
                                              overallScore >= 65 ? '#e91e63' : 
                                              overallScore >= 50 ? '#ffd93d' : '#ff6b9d';
                            
                            return (
                            <div key={analysis.id} className="business-analyse-card">
                                <div className="business-analyse-card-header">
                                    <h3 className="business-analyse-card-title">{analysis.name}</h3>
                                    <div className="business-analyse-card-actions">
                                        <button 
                                            className="business-analyse-action-btn info"
                                            onClick={() => handleShowJson(analysis.id)}
                                            title="JSON Daten anzeigen"
                                        >
                                            <i className="fas fa-code"></i>
                                        </button>
                                        <button 
                                            className="business-analyse-action-btn edit"
                                            onClick={() => navigate(`/business-analysis/edit/${analysis.id}`)}
                                            title="Analyse bearbeiten"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button 
                                            className="business-analyse-action-btn delete"
                                            onClick={() => handleDeleteAnalysis(analysis.id)}
                                            title="Analyse löschen"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="business-analyse-card-content">
                                    {analysis.targetAudience && (
                                        <p className="business-analyse-description">
                                            {typeof analysis.targetAudience === 'object' 
                                                ? (analysis.targetAudience.description || 
                                                   `${analysis.targetAudience.ageRange || ''} Jahre, ${
                                                       analysis.targetAudience.gender === 'all' ? 'Alle Geschlechter' :
                                                       analysis.targetAudience.gender === 'male' ? 'Männlich' :
                                                       analysis.targetAudience.gender === 'female' ? 'Weiblich' : ''
                                                   }`)
                                                : analysis.targetAudience}
                                        </p>
                                    )}
                                    
                                    <div className="business-analyse-meta">
                                        {analysis.niche && (
                                            <span className="business-analyse-tag">
                                                <i className="fas fa-bullseye"></i>
                                                {analysis.niche}
                                            </span>
                                        )}
                                        <span className="business-analyse-tag">
                                            <i className="fas fa-building"></i>
                                            {analysis.brandType === 'personal' ? 'Personal Brand' : 'Corporate Brand'}
                                        </span>
                                        <span className="business-analyse-date">
                                            <i className="fas fa-calendar"></i>
                                            {new Date(analysis.createdAt).toLocaleDateString('de-DE')}
                                        </span>
                                    </div>
                                    
                                    <div className="business-analyse-stats">
                                        <div className="business-analyse-stat">
                                            <i className="fas fa-funnel-dollar"></i>
                                            <span>{analysis.leadSourcesCount} Lead-Quellen</span>
                                        </div>
                                        <div className="business-analyse-stat">
                                            <i className="fas fa-box"></i>
                                            <span>{analysis.productsCount} Produkte</span>
                                        </div>
                                    </div>
                                    
                                    <div className="business-analyse-completion">
                                        <div className="business-analyse-completion-label">
                                            Gesamtbewertung: {overallScore}/100
                                        </div>
                                        <div className="business-analyse-completion-bar">
                                            <div 
                                                className="business-analyse-completion-fill"
                                                style={{ 
                                                    width: `${overallScore}%`,
                                                    background: `linear-gradient(90deg, 
                                                        #1a1a1a 0%,
                                                        ${scoreColor} 20%,
                                                        ${scoreColor} 80%,
                                                        #1a1a1a 100%)`
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="business-analyse-card-footer">
                                    <span className={`business-analyse-status ${analysis.completionScore >= 80 ? 'completed' : 'draft'}`}>
                                        {analysis.completionScore}% Vollständig
                                    </span>
                                    <button 
                                        className="business-analyse-view-btn"
                                        onClick={() => handleViewAnalysis(analysis.id)}
                                    >
                                        Link
                                        <i className="fas fa-eye"></i>
                                    </button>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* JSON Modal */}
            {showJsonModal && selectedAnalysisJson && (
                <div className="business-analyse-modal-overlay" onClick={handleCloseJsonModal}>
                    <div className="business-analyse-json-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="business-analyse-modal-header">
                            <h2>
                                <i className="fas fa-code"></i>
                                Analyse Daten (JSON)
                            </h2>
                            <button 
                                className="business-analyse-modal-close"
                                onClick={handleCloseJsonModal}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="business-analyse-json-content">
                            <div className="business-analyse-json-header">
                                <div className="business-analyse-json-info">
                                    <span className="business-analyse-json-badge">
                                        <i className="fas fa-database"></i>
                                        {selectedAnalysisJson.name || 'Unbenannte Analyse'}
                                    </span>
                                    <span className="business-analyse-json-size">
                                        {JSON.stringify(selectedAnalysisJson).length} Zeichen
                                    </span>
                                </div>
                                <button 
                                    className="business-analyse-copy-btn"
                                    onClick={handleCopyJson}
                                >
                                    <i className="fas fa-copy"></i>
                                    Kopieren
                                </button>
                            </div>
                            
                            <pre className="business-analyse-json-display">
                                <code>{JSON.stringify(selectedAnalysisJson, null, 2)}</code>
                            </pre>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default BusinessAnalysisDashboard;
